import styled from '@emotion/styled'
import { memo, type ReactNode } from 'react'

import { type UiContext, useUiContext } from '../../code-only/hooks/index.ts'
import { spacing } from '../../foundations/index.ts'
import { Text } from '../../foundations/text/index.ts'

import { HeaderCustomerLayout, type HeaderStatus } from './header-customer.tsx'

export type HeaderProps = {
  // common props
  title?: string | undefined
  forceScreenVariant?: UiContext | undefined

  // backoffice props
  badge?: ReactNode | undefined
  leftButton?: ReactNode | undefined
  rightButtonGroups?: ReactNode[] | undefined

  // customer props
  /**
   * Compact mode
   * When true, shows the HeaderSeparator
   * When false, shows the a progress bar
   */
  compact?: boolean | undefined
  /**
   * Progress bar value between 0 and 1
   */
  progress?: number | undefined
  /**
   * Progress bar initial value
   */
  initialProgress?: number | undefined
  /**
   * right block to display instead of the default ContactButton
   */
  rightBlock?: ReactNode | undefined
  onContact?: (() => void) | undefined
  /**
   * Header status: locked or under modification show a chip.
   */
  headerStatus?: HeaderStatus | undefined
  /**
   * Optional chip rendered in the header center area (alongside any headerStatus chip).
   */
  centerChip?: ReactNode | undefined
  /**
   * Optional language selector rendered just left of the right block / contact button.
   */
  languageSelector?: ReactNode | undefined
}

export const Header = memo<HeaderProps>(function Header(props: HeaderProps) {
  const {
    title,
    badge,
    leftButton,
    rightButtonGroups,
    compact,
    progress,
    initialProgress,
    rightBlock,
    onContact,
    forceScreenVariant,
    headerStatus,
    centerChip,
    languageSelector,
  } = props

  const screenVariant = useUiContext()
  const actualScreenVariant = forceScreenVariant ?? screenVariant
  const isBackofficeVariant = actualScreenVariant === 'backoffice'

  return isBackofficeVariant ? (
    <HeaderContainer>
      {leftButton}
      <MainContentContainer>
        <TitleText variant="h6">{title}</TitleText>
        <StyledBadgeContainer>{badge}</StyledBadgeContainer>
      </MainContentContainer>
      {rightButtonGroups}
    </HeaderContainer>
  ) : (
    <HeaderCustomerLayout
      title={title}
      compact={compact}
      progress={progress}
      initialProgress={initialProgress}
      rightBlock={rightBlock}
      onContact={onContact}
      headerStatus={headerStatus}
      centerChip={centerChip}
      languageSelector={languageSelector}
    />
  )
})

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;

  gap: ${spacing[50]};
`

const MainContentContainer = styled.div`
  overflow: hidden;

  display: flex;
  flex: 1;

  align-items: center;
  gap: ${spacing[50]};
`

const StyledBadgeContainer = styled.div`
  display: flex;
  gap: ${spacing[30]};
  align-items: center;
`

const TitleText = styled(Text)`
  white-space: pre-wrap;
  overflow: hidden;
  text-overflow: ellipsis;
`
